// src/contexts/EntityFiltersContext/EntityFiltersContext.tsx
// Central filter state provider with URL and localStorage synchronization
//
// Synchronization strategy:
//
// 1. INITIALIZATION (mount + userId change):
//    - Merge: defaults < localStorage < URL  (URL wins)
//    - This is the ONLY time localStorage is read into state
//
// 2. STATE → URL sync (filters change):
//    - Save to localStorage first (so storage is always fresh)
//    - Then update URL search params using navigate() with the current
//      committed pathname from useLocation(). This prevents the race
//      condition where setSearchParams would append query params to a
//      stale pathname during route transitions.
//
// 3. URL → STATE sync (browser back/forward):
//    - Parse filters from URL only — NO localStorage merge
//    - This prevents stale localStorage values from overwriting
//      freshly committed filter changes
//
// Vehicle selection (mobile):
//    - persistVehicleId saves carId to both state and localStorage
//    - restoreVehicleId reads from localStorage and validates
//    - These are used by the mobile vehicle switcher for cross-session
//      persistence of the active car selection

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";

import { EntityFilters, DEFAULT_ENTITY_FILTERS } from "@/types/filters";
import {
  parseFiltersFromUrl,
  buildSearchParams,
  saveFiltersToStorage,
  loadFiltersFromStorage,
  clearFiltersFromStorage,
  toExpenseFilter,
  toTravelFilter,
  toRevenueFilter,
  hasActiveFilters as checkHasActiveFilters,
  mergeWithDefaults,
} from "@/utils/filterHelpers";

import { ExpenseFilter } from "@/utils/api/ExpenseService";
import { TravelFilter } from "@/utils/api/TravelService";

import { useApp } from "@/contexts/AppContext";

// ============================================
// Types
// ============================================

export interface EntityFiltersContextValue {
  /** Current filter state */
  filters: EntityFilters;

  /** Whether any non-default filters are active */
  hasActiveFilters: boolean;

  /** Whether the context has been initialized from URL/storage */
  isInitialized: boolean;

  // ============================================
  // Individual Setters
  // ============================================

  /** Set selected car ID */
  setCarId: (carId: string | null) => void;

  /** Set date range */
  setDateRange: (dateFrom: string | null, dateTo: string | null) => void;

  /** Set selected tag IDs */
  setTagIds: (tagIds: string[]) => void;

  /** Set selected year */
  setYear: (year: number | null) => void;

  /** Set selected expense type */
  setExpenseType: (expenseType: number | null) => void;

  // ============================================
  // Bulk Operations
  // ============================================

  /** Update multiple filters at once */
  setFilters: (updates: Partial<EntityFilters>) => void;

  /** Clear all filters, optionally keeping specific ones */
  clearFilters: () => void;

  /** Reset to default values */
  resetToDefaults: () => void;

  // ============================================
  // Vehicle Selection (Mobile)
  // ============================================

  /**
   * Set the selected vehicle and persist the choice to device storage.
   * Used by the mobile vehicle switcher to select a vehicle.
   * This sets the carId filter AND saves it to localStorage scoped by userId,
   * so it survives across sessions on the same device.
   *
   * @param carId - The vehicle ID to select, or null to clear selection
   */
  persistVehicleId: (carId: string | null) => void;

  /**
   * Restore the previously selected vehicle from device storage.
   * Called by DashboardProvider after cars are loaded to validate
   * and apply the stored selection.
   *
   * - Reads carId from user-scoped localStorage
   * - Validates it against the provided list of valid car IDs
   * - If valid, sets it as the active carId filter
   * - If invalid (car deleted, access revoked), clears the stored value
   *
   * @param validCarIds - Array of car IDs the user currently has access to
   * @returns The restored carId if valid, or null if no valid selection exists
   */
  restoreVehicleId: (validCarIds: string[]) => string | null;

  // ============================================
  // API Filter Conversion
  // ============================================

  /** Convert to ExpenseFilter for API calls (generic, requires expenseType) */
  getExpenseFilter: (expenseType?: number[]) => Partial<ExpenseFilter>;

  /** Convert to ExpenseFilter for refuels (expenseType = 1) */
  getRefuelFilter: () => Partial<ExpenseFilter>;

  /** Convert to ExpenseFilter for revenues (expenseType = 5) */
  getRevenueFilter: () => Partial<ExpenseFilter>;

  /** Convert to TravelFilter for API calls */
  getTravelFilter: () => Partial<TravelFilter>;
}

// ============================================
// Context
// ============================================

const EntityFiltersContext = createContext<
  EntityFiltersContextValue | undefined
>(undefined);

// ============================================
// Provider Props
// ============================================

export interface EntityFiltersProviderProps {
  children: React.ReactNode;
}

// ============================================
// Provider
// ============================================

export const EntityFiltersProvider: React.FC<EntityFiltersProviderProps> = ({
  children,
}) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [isInitialized, setIsInitialized] = useState(false);
  const isUpdatingUrl = useRef(false);
  const previousFiltersRef = useRef<EntityFilters | null>(null);

  // Get userId for scoped storage
  const { user } = useApp();
  const userId = user?.id ?? null;
  const previousUserIdRef = useRef<string | null | undefined>(undefined);

  // Initialize filters from URL > localStorage > defaults
  // Note: On first render, userId may be null (auth initializing).
  // We load from unscoped storage initially; once userId becomes available,
  // a useEffect re-loads from the user-scoped storage.
  const [filters, setFiltersState] = useState<EntityFilters>(() => {
    // First, try to get from URL
    const urlFilters = parseFiltersFromUrl(searchParams);

    // Then, load from localStorage (no userId yet on first render)
    const storedFilters = loadFiltersFromStorage();

    // Merge: defaults < stored < URL (URL takes highest precedence)
    return mergeWithDefaults({
      ...storedFilters,
      ...urlFilters,
    });
  });

  // Mark as initialized after first render
  useEffect(() => {
    setIsInitialized(true);
  }, []);

  // ============================================
  // Re-initialize from user-scoped storage when userId becomes available
  // ============================================

  useEffect(() => {
    const previousUserId = previousUserIdRef.current;
    previousUserIdRef.current = userId;

    // Skip initial render (previousUserId is undefined)
    if (previousUserId === undefined) {
      // If userId is already available on first effect run, reload from scoped storage
      if (userId) {
        const urlFilters = parseFiltersFromUrl(searchParams);
        const storedFilters = loadFiltersFromStorage(userId);
        const merged = mergeWithDefaults({
          ...storedFilters,
          ...urlFilters,
        });

        isUpdatingUrl.current = true;
        setFiltersState(merged);
        setTimeout(() => {
          isUpdatingUrl.current = false;
        }, 0);
      }
      return;
    }

    // User changed (sign-in, sign-out, account switch)
    if (previousUserId !== userId) {
      if (userId) {
        // New user signed in — load their scoped filters
        const urlFilters = parseFiltersFromUrl(searchParams);
        const storedFilters = loadFiltersFromStorage(userId);
        const merged = mergeWithDefaults({
          ...storedFilters,
          ...urlFilters,
        });

        isUpdatingUrl.current = true;
        setFiltersState(merged);
        setTimeout(() => {
          isUpdatingUrl.current = false;
        }, 0);
      } else {
        // User signed out — reset to defaults
        isUpdatingUrl.current = true;
        setFiltersState(DEFAULT_ENTITY_FILTERS);
        setTimeout(() => {
          isUpdatingUrl.current = false;
        }, 0);
      }
    }
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ============================================
  // STATE → URL sync: when filters change, update URL and save to storage
  //
  // Uses navigate() instead of setSearchParams() to control the full URL
  // (pathname + search). setSearchParams() only updates search params and
  // uses whatever pathname React Router currently has — during route
  // transitions this can be a stale pathname (e.g. /cars/add instead of
  // /cars), causing incorrect redirects.
  //
  // navigate() with location.pathname uses the committed React Router
  // location, which is always the current resolved pathname.
  // ============================================

  useEffect(() => {
    // Skip if we're currently processing a URL update
    if (isUpdatingUrl.current) {
      return;
    }

    // Skip if filters haven't actually changed
    if (
      previousFiltersRef.current &&
      JSON.stringify(previousFiltersRef.current) === JSON.stringify(filters)
    ) {
      return;
    }

    previousFiltersRef.current = filters;

    // Save to localStorage FIRST so it's fresh before any URL listener
    // fires in response to the navigate() call below.
    saveFiltersToStorage(filters, userId);

    // Build new search params from filters
    const newParams = buildSearchParams(filters);
    const newSearch = newParams.toString();
    const currentSearch = searchParams.toString();

    // Only update URL if search params actually changed.
    // This prevents unnecessary history entries and re-renders.
    if (newSearch !== currentSearch) {
      // Set the flag BEFORE calling navigate so the URL → STATE listener
      // (which may fire synchronously or in the same microtask) sees it.
      isUpdatingUrl.current = true;

      // Use navigate() with the committed pathname from useLocation().
      // This ensures we always write search params onto the correct,
      // current pathname — not a stale one from a mid-transition route.
      navigate(
        {
          pathname: location.pathname,
          search: newSearch ? `?${newSearch}` : "",
        },
        { replace: true }
      );

      // Reset flag after React has processed the URL update
      setTimeout(() => {
        isUpdatingUrl.current = false;
      }, 0);
    }
  }, [filters, searchParams, navigate, location.pathname, userId]);

  // ============================================
  // URL → STATE sync: browser back/forward only
  //
  // IMPORTANT: This effect only reads from the URL — it does NOT merge
  // with localStorage. localStorage is only consulted during:
  //   - Initial mount (useState initializer)
  //   - userId change (re-initialization effect above)
  //   - restoreVehicleId (explicit mobile vehicle restoration)
  //
  // Previous behavior merged with localStorage on every URL change,
  // which caused stale carId values from storage to overwriting freshly
  // committed filter changes (the "carId oscillation" bug).
  // ============================================

  useEffect(() => {
    // Skip if this URL change was triggered by our own state → URL sync
    if (isUpdatingUrl.current) {
      return;
    }

    // Parse filters from URL only — no localStorage merge
    const urlFilters = parseFiltersFromUrl(searchParams);
    const mergedFilters = mergeWithDefaults(urlFilters);

    // Only update if filters actually changed
    if (JSON.stringify(mergedFilters) !== JSON.stringify(filters)) {
      isUpdatingUrl.current = true;
      setFiltersState(mergedFilters);
      // Reset flag after state update
      setTimeout(() => {
        isUpdatingUrl.current = false;
      }, 0);
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  // ============================================
  // Individual Setters
  // ============================================

  const setCarId = useCallback((carId: string | null) => {
    setFiltersState((prev) => ({ ...prev, carId }));
  }, []);

  const setDateRange = useCallback(
    (dateFrom: string | null, dateTo: string | null) => {
      setFiltersState((prev) => ({ ...prev, dateFrom, dateTo }));
    },
    []
  );

  const setTagIds = useCallback((tagIds: string[]) => {
    setFiltersState((prev) => ({ ...prev, tagIds }));
  }, []);

  const setYear = useCallback((year: number | null) => {
    setFiltersState((prev) => ({ ...prev, year }));
  }, []);

  const setExpenseType = useCallback((expenseType: number | null) => {
    setFiltersState((prev) => ({ ...prev, expenseType }));
  }, []);

  // ============================================
  // Bulk Operations
  // ============================================

  const setFilters = useCallback((updates: Partial<EntityFilters>) => {
    setFiltersState((prev) => ({
      ...prev,
      ...updates,
      // Ensure tagIds is always an array
      tagIds: updates.tagIds ?? prev.tagIds,
    }));
  }, []);

  const clearFilters = useCallback(() => {
    setFiltersState(DEFAULT_ENTITY_FILTERS);
    clearFiltersFromStorage(userId);
  }, [userId]);

  const resetToDefaults = useCallback(() => {
    setFiltersState(DEFAULT_ENTITY_FILTERS);
    clearFiltersFromStorage(userId);
  }, [userId]);

  // ============================================
  // Vehicle Selection (Mobile)
  // ============================================

  const persistVehicleId = useCallback(
    (carId: string | null) => {
      // Update carId in filters (triggers URL sync and data reload)
      setFiltersState((prev) => ({ ...prev, carId }));

      // Explicitly save to user-scoped storage immediately so the
      // vehicle selection is persisted even if the component unmounts
      // before the filters→storage sync effect runs.
      setFiltersState((prev) => {
        const updated = { ...prev, carId };
        saveFiltersToStorage(updated, userId);
        return updated;
      });
    },
    [userId]
  );

  const restoreVehicleId = useCallback(
    (validCarIds: string[]): string | null => {
      // Load the stored filters to get the persisted carId
      const storedFilters = loadFiltersFromStorage(userId);
      const storedCarId = storedFilters.carId ?? null;

      if (!storedCarId) {
        return null;
      }

      // Validate against the current list of accessible cars
      if (validCarIds.includes(storedCarId)) {
        // Valid — apply it to filters if not already set
        setFiltersState((prev) => {
          if (prev.carId !== storedCarId) {
            return { ...prev, carId: storedCarId };
          }
          return prev;
        });
        return storedCarId;
      }

      // Stale — car no longer accessible, clear from storage
      setFiltersState((prev) => {
        if (prev.carId === storedCarId) {
          const updated = { ...prev, carId: null };
          saveFiltersToStorage(updated, userId);
          return updated;
        }
        return prev;
      });

      return null;
    },
    [userId]
  );

  // ============================================
  // API Filter Conversion
  // ============================================

  const getExpenseFilter = useCallback(
    (expenseType?: number[]): Partial<ExpenseFilter> => {
      return toExpenseFilter(filters, expenseType);
    },
    [filters]
  );

  const getRefuelFilter = useCallback((): Partial<ExpenseFilter> => {
    return toExpenseFilter(filters, [1]); // EXPENSE_TYPE.REFUEL = 1
  }, [filters]);

  const getRevenueFilter = useCallback((): Partial<ExpenseFilter> => {
    return toRevenueFilter(filters);
  }, [filters]);

  const getTravelFilter = useCallback((): Partial<TravelFilter> => {
    return toTravelFilter(filters);
  }, [filters]);

  // ============================================
  // Computed Values
  // ============================================

  const hasActiveFilters = useMemo(
    () => checkHasActiveFilters(filters),
    [filters]
  );

  // ============================================
  // Context Value
  // ============================================

  const contextValue = useMemo<EntityFiltersContextValue>(
    () => ({
      filters,
      hasActiveFilters,
      isInitialized,

      // Individual setters
      setCarId,
      setDateRange,
      setTagIds,
      setYear,
      setExpenseType,

      // Bulk operations
      setFilters,
      clearFilters,
      resetToDefaults,

      // Vehicle selection
      persistVehicleId,
      restoreVehicleId,

      // API conversion
      getExpenseFilter,
      getRefuelFilter,
      getRevenueFilter,
      getTravelFilter,
    }),
    [
      filters,
      hasActiveFilters,
      isInitialized,
      setCarId,
      setDateRange,
      setTagIds,
      setYear,
      setExpenseType,
      setFilters,
      clearFilters,
      resetToDefaults,
      persistVehicleId,
      restoreVehicleId,
      getExpenseFilter,
      getRefuelFilter,
      getRevenueFilter,
      getTravelFilter,
    ]
  );

  return (
    <EntityFiltersContext.Provider value={contextValue}>
      {children}
    </EntityFiltersContext.Provider>
  );
};

// ============================================
// Hook
// ============================================

/**
 * Hook to access entity filters from context.
 * Must be used within EntityFiltersProvider.
 */
export const useEntityFiltersContext = (): EntityFiltersContextValue => {
  const context = useContext(EntityFiltersContext);

  if (context === undefined) {
    throw new Error(
      "useEntityFiltersContext must be used within an EntityFiltersProvider"
    );
  }

  return context;
};